import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { INCOMMODITY_SATISFACTION_MANAGER } from "../../../consts/incommodity";
import { HASH_SATISFACTION } from "../../../consts/hash";

import {
  calculateTimeDifference,
  convertFullDateTimeToWithoutSecond,
  getYearToMinuteFromISODateString,
} from "../../../libs/dateFormat";

import { useUserContext } from "../../../contexts/userContext";
import {
  REPORT_STATUS,
  ReportDetailItem,
  getManagerReportDetail,
} from "../../../services/mangerReportServices";
import {
  SatisfactionRequest,
  deleteReport,
  postSatisfaction,
} from "../../../services/userReportServices";
import { patchEtcSatisfaction } from "../../../services/etcServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";

import CommonButton from "../../../components/Button";
import StickyHeader from "../../../components/Layout/StickyHeader";
import ProgressBar from "../../../components/ProgressBar";
import HorizontalRule from "../../../components/Rule/Horizontal";
import CommonPopup from "../../../components/Popup";
import HistorySatisfaction from "../../../components/History/Satisfaction";
import BottomGroup from "../../../components/Button/Group";
import ImagePreview from "../../../components/Input/ImagePreview";
import ManagerReportBeforeAfterImage from "../../../components/Manager/Report/BeforeAfterImage";
import UserSatisfactionPopup from "../../../components/Popup/UserSatisfaction";
import HistoryDetailRow from "../../../components/History/DetailRow";
import HistoryDetailBlock from "../../../components/History/DetailBlock";
import CommonProfile from "../../../components/MyInfo/Profile";

type Props = {};

type PopupType = "cancelConfirm" | "satisfaction" | "";

export default function HistoryReport(props: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const [user] = useUserContext();
  const [reportDetail, setReportDetail] = useState<ReportDetailItem>();
  const [popup, setPopup] = useState<PopupType>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const location = useLocation();

  const loadManagerReportDetail = async () => {
    setIsLoading(true);
    try {
      const res = await getManagerReportDetail({
        centerIncommodityId: Number(params.id),
      });
      if (res.status === HttpStatusCode.Ok) {
        setReportDetail(res.data);
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("히스토리 상세 조회에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusStep = () => {
    switch (reportDetail?.status) {
      case REPORT_STATUS.REQUESTED.toString():
        return 1;
      case REPORT_STATUS.PROCESSING.toString():
      case REPORT_STATUS.SCHEDULED.toString():
        return 2;
      case REPORT_STATUS.COMPLETED.toString():
      case REPORT_STATUS.COMPLETED2.toString():
        return 3;
      case REPORT_STATUS.CANCELED.toString():
      case REPORT_STATUS.CANCELED2.toString():
      default:
        return 0;
    }
  };

  const getStatusBottomButton = () => {
    switch (reportDetail?.status) {
      case REPORT_STATUS.REQUESTED.toString():
        return (
          <div className="bg-white">
            <CommonButton
              className="w-full rounded-none text-sm px-4 py-3 h-bottom-button"
              onClick={() => {
                setPopup("cancelConfirm");
              }}
            >
              접수 취소
            </CommonButton>
          </div>
        );
      case REPORT_STATUS.COMPLETED.toString():
        return (
          !reportDetail.satisfactionId && (
            <div className="bg-white">
              <CommonButton
                className="w-full rounded-none text-sm px-4 py-3 h-bottom-button"
                onClick={() => {
                  setPopup("satisfaction");
                }}
              >
                만족도 평가
              </CommonButton>
            </div>
          )
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (user.isLoading) return;
    if (reportDetail?.createdBy && reportDetail?.createdBy !== user.memberId) {
      navigate("/history", {
        state: {
          error: "다른 사용자의 불편 신고 정보는 조회할 수 없습니다.",
        },
      });
    }
  }, [reportDetail, user.isLoading]);

  useEffect(() => {
    if (user.isLoading) return;
    loadManagerReportDetail();
  }, [params.id, user.isLoading]);

  useEffect(() => {
    const isSatisfactionHash = location.hash === HASH_SATISFACTION;
    const statusValid =
      reportDetail &&
      [
        REPORT_STATUS.COMPLETED.toString(),
        REPORT_STATUS.COMPLETED2.toString(),
      ].includes(reportDetail.status) &&
      !reportDetail?.satisfactionId;

    if (isSatisfactionHash && statusValid) {
      setPopup("satisfaction");
    }
  }, [location.hash, reportDetail]);

  return (
    <div>
      {loadingComponent}
      <StickyHeader
        title="히스토리 상세"
        onBackClick={() =>
          navigate(location?.state?.parentUrl ?? "/history", {
            state: { ...location?.state?.parentState },
          })
        }
        className="px-5"
      />
      <div className="flex flex-col gap-2.5 p-5">
        <div className="text-3xl ">{reportDetail?.smallCategory}</div>
        <div className="text-base font-normal text-brand-neutral-40">
          {reportDetail?.largeCategory}
        </div>
      </div>
      <div className="flex flex-col gap-2.5 pt-0 px-5 pb-[30px]">
        <div
          className="p-5 flex flex-col gap-2.5 rounded-[10px]"
          style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.10)" }}
        >
          <div className=" bg-brand-floating-primary-button bg-clip-text text-base text-transparent font-semibold">
            {reportDetail?.statusName}
          </div>
          <ProgressBar
            currentStep={getStatusStep()}
            wrapperClassName="!p-0"
            className="h-2.5 !bg-brand-floating-primary-button"
            rounded
            nonSticky
          />

          {reportDetail?.feedback && (
            <>
              <HorizontalRule className="!mt-[5px] !mb-[5px]" />
              <div className="flex gap-2.5 items-center">
                <CommonProfile src={reportDetail?.completedByThumbnail} />
                <div className="flex flex-col">
                  <div className="text-sm font-semibold">
                    {reportDetail.completedByName}
                  </div>
                  <div className=" text-xs text-brand-neutral-30">
                    {reportDetail.completedAt
                      ?.split(":")
                      .splice(0, 2)
                      .join(":")}
                  </div>
                </div>
              </div>
              <HorizontalRule className="!mt-[5px] !mb-[5px]" />
              <div className=" text-sm font-bold">{reportDetail.feedback}</div>
              <ManagerReportBeforeAfterImage reportDetail={reportDetail} />
              {[
                REPORT_STATUS.COMPLETED.toString(),
                REPORT_STATUS.COMPLETED2.toString(),
              ].includes(reportDetail?.status) && (
                <>
                  <HorizontalRule className="!mt-[5px] !mb-[5px]" />
                  {reportDetail.satisfactionId ? (
                    <HistorySatisfaction
                      satisfaction={{
                        id: reportDetail.satisfactionId,
                        score: reportDetail.satisfaction,
                        comment: reportDetail.satisfactionComment,
                        createdAt: reportDetail.satisfactionCreatedAt,
                        createdBy: reportDetail.satisfactionCreatedBy,
                        createdByName: reportDetail.satisfactionCreatedByName,
                        createdByThumbnail:
                          reportDetail.satisfactionCreatedByThumbnail,
                        reply:
                          reportDetail.satisfactionReplyId &&
                          reportDetail.satisfactionReplyId > 0
                            ? {
                                id: reportDetail.satisfactionReplyId,
                                comment: reportDetail.satisfactionReplyComment,
                                createdAt:
                                  reportDetail.satisfactionReplyCreatedAt,
                                createdBy:
                                  reportDetail.satisfactionReplyCreatedBy,
                                createdByName:
                                  INCOMMODITY_SATISFACTION_MANAGER.NAME,
                                createdByThumbnail:
                                  reportDetail.satisfactionReplyCreatedByThumbnail,
                              }
                            : undefined,
                      }}
                      type="incommodity"
                      serviceId={reportDetail.centerIncommodityId.toString()}
                      setIsLoading={setIsLoading}
                      open={open}
                      postSatisfaction={async (
                        satisfactionId: number,
                        satisfaction: SatisfactionRequest
                      ) => {
                        await patchEtcSatisfaction(satisfactionId, {
                          ...satisfaction,
                          serviceType: "incommodity",
                        });
                        await loadManagerReportDetail();
                      }}
                    />
                  ) : (
                    <CommonButton
                      variant="secondary"
                      className="px-4 py-[11px] text-sm"
                      onClick={() => {
                        setPopup("satisfaction");
                      }}
                    >
                      만족도 평가하기
                    </CommonButton>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="px-5 flex flex-col gap-10">
        <HistoryDetailBlock title="접수 정보">
          <HistoryDetailRow title="분류">
            <div className="font-bold">{reportDetail?.largeCategory}</div>
            {reportDetail?.middleCategory && (
              <div className="bg-brand-category_tag rounded py-1 pl-1.5 pr-2 flex items-center gap-1.5 text-sm w-fit">
                <div className="w-1 h-1 bg-white rounded-full"></div>
                {reportDetail?.middleCategory}
              </div>
            )}
            <div>{reportDetail?.smallCategory}</div>
          </HistoryDetailRow>
          <HistoryDetailRow title="접수 시간">
            {reportDetail?.createdAt &&
              getYearToMinuteFromISODateString(reportDetail?.createdAt)}
          </HistoryDetailRow>
          <HistoryDetailRow title="접수 번호">
            <strong>{reportDetail?.centerIncommodityId}</strong>
          </HistoryDetailRow>
          {/* TODO: 통근버스의 경우 접수 정보중 접수 번호까지만 출력 */}
          <HistoryDetailRow title="접수 위치">
            <div>
              {reportDetail?.buildingName} {reportDetail?.floorName} /{" "}
              {reportDetail?.createdByCompany}
            </div>
            <div>{reportDetail?.location}</div>
          </HistoryDetailRow>
          <HistoryDetailRow title="상세 내용">
            <div>{reportDetail?.categoryName}</div>
            <div>{reportDetail?.description}</div>
          </HistoryDetailRow>
          {/* TODO: 위험신고 개선제안은 처리 희망 시간 출력 제거 */}
          <HistoryDetailRow title="처리 희망시간">
            {!reportDetail?.deadLine
              ? "즉시 신청"
              : convertFullDateTimeToWithoutSecond(reportDetail?.deadLine)}
          </HistoryDetailRow>

          {reportDetail?.userFile?.filePath && (
            <>
              <HorizontalRule margin="0" />
              <div className="py-1.5">
                <div className="py-3 text-sm text-brand-neutral-60 w-[90px] min-w-[90px]">
                  접수 사진
                </div>
                <ImagePreview
                  className="max-h-[calc((100vw-20px)/3)] max-w-[calc((100vw-20px)/3)]"
                  image={reportDetail?.userFile?.filePath}
                  imageList={[reportDetail?.userFile?.filePath]}
                  index={0}
                  wrapperClassName="max-w-[calc((100vw-20px)/3)]"
                />
              </div>
            </>
          )}
        </HistoryDetailBlock>

        {Number(reportDetail?.status) >= REPORT_STATUS.PROCESSING &&
          Number(reportDetail?.status) < REPORT_STATUS.CANCELED2 &&
          reportDetail?.receiptedAt && (
            <HistoryDetailBlock title="처리/결과">
              <HistoryDetailRow title="접수 확인 시간">
                {convertFullDateTimeToWithoutSecond(reportDetail.receiptedAt)}
              </HistoryDetailRow>

              {Number(reportDetail?.status) >= REPORT_STATUS.COMPLETED2 && (
                <>
                  <HistoryDetailRow title="처리 완료 시간">
                    <div className="text-[#194BDC]">
                      {reportDetail.completedAt &&
                        convertFullDateTimeToWithoutSecond(
                          reportDetail.completedAt
                        )}
                    </div>
                    <div>
                      (
                      {reportDetail.completedAt &&
                        calculateTimeDifference(
                          reportDetail.receiptedAt,
                          reportDetail.completedAt
                        )}{" "}
                      소요)
                    </div>
                  </HistoryDetailRow>

                  <HistoryDetailRow title="피드백">
                    {reportDetail.feedback}
                  </HistoryDetailRow>
                </>
              )}
            </HistoryDetailBlock>
          )}

        {REPORT_STATUS.CANCELED.toString() === reportDetail?.status &&
          reportDetail?.canceledAt && (
            <HistoryDetailBlock title="취소 정보">
              <HistoryDetailRow title="취소 시간">
                {convertFullDateTimeToWithoutSecond(
                  reportDetail?.canceledAt ?? ""
                )}
              </HistoryDetailRow>
            </HistoryDetailBlock>
          )}

        {getStatusBottomButton() && (
          <BottomGroup>{getStatusBottomButton()}</BottomGroup>
        )}
      </div>

      <CommonPopup isOpen={popup === "cancelConfirm"} padding={0}>
        <div className="p-5 pt-0 text-xl font-medium flex items-center justify-center h-[110px]">
          접수를 취소하시겠습니까?
        </div>
        <div className="w-full flex">
          <CommonButton
            variant="secondary"
            className="rounded-none w-[100px] h-bottom-button"
            onClick={() => {
              setPopup("");
            }}
          >
            취소
          </CommonButton>
          <CommonButton
            className="rounded-none flex-1 h-bottom-button"
            onClick={async () => {
              setIsLoading(true);
              try {
                const res = await deleteReport({
                  centerIncommodityId: Number(params.id),
                });
                if (res.status === HttpStatusCode.Ok) {
                  open("접수가 취소되었습니다.");
                  loadManagerReportDetail();
                }
              } catch (e: any) {
                if (e?.response?.data?.error) {
                  open(e.response.data.error?.replace(". ", ".\n"));
                } else {
                  open("접수 취소에 실패했습니다.");
                }
              } finally {
                setIsLoading(false);
                setPopup("");
              }
            }}
          >
            확인
          </CommonButton>
        </div>
      </CommonPopup>

      <UserSatisfactionPopup
        popup={popup}
        setPopup={(popup) => setPopup(popup as PopupType)}
        setIsLoading={(isLoading) => setIsLoading(isLoading)}
        open={open}
        postSatisfaction={async (satisfaction: SatisfactionRequest) => {
          await postSatisfaction(Number(params.id), satisfaction);
          await loadManagerReportDetail();
        }}
      />

      {simplePopupComponent}
    </div>
  );
}

export const getVCString = (id?: number) => {
  if (!id) return;
  return "VC_" + String(id).padStart(7, "0");
};

export const ManagerReportDetailCommentIcon = () => (
  <svg
    width="60"
    height="61"
    viewBox="0 0 60 61"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.3">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M45.4093 46.2478C40.7911 50.8666 34.7121 53.1031 28.6712 52.9725C20.4675 52.7952 6.0791 52.924 6.0791 52.924C6.0791 52.924 6.20527 38.367 6.19907 30.0207C6.19471 24.1391 8.43411 18.2587 12.9294 13.7644C21.8902 4.80029 36.4486 4.80029 45.4093 13.762C54.3862 22.74 54.3701 37.2861 45.4093 46.2478Z"
        stroke="black"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M19.4767 31.0054H19.2187"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M29.2355 31.0054H28.9775"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M38.9943 31.0054H38.7363"
        stroke="black"
        strokeWidth="3"
        strokeLinecap="square"
      />
      <path
        d="M42.6981 23.9039C48.6086 23.9039 53.4 19.1125 53.4 13.202C53.4 7.29143 48.6086 2.5 42.6981 2.5C36.7875 2.5 31.9961 7.29143 31.9961 13.202C31.9961 19.1125 36.7875 23.9039 42.6981 23.9039Z"
        fill="black"
        stroke="white"
        strokeWidth="4"
      />
      <path
        d="M45.9508 9.94922L39.4453 16.4547"
        stroke="white"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M39.4453 9.94922L45.9508 16.4547"
        stroke="white"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const ManagerReportDetailRemoveIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y="0.5" width="24" height="24" rx="12" fill="#E7E7E8" />
    <g opacity="0.3">
      <path
        d="M16.5787 7.64941L7.37793 16.8502"
        stroke="black"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
      <path
        d="M7.37793 7.64941L16.5787 16.8502"
        stroke="black"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);
